import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
	Alert,
	Box,
	Button,
	MenuItem,
	Paper,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import {
	CalendarDays,
	CheckCircle2,
	ChevronDown,
	Flame,
	HelpCircle,
	Target,
	TrendingUp,
} from "lucide-react";
import { getStatisticsDashboard } from "utils/supabase";
import LogoLoader from "components/Custom/LogoLoader.js";

const activityColors = {
	exam: "#8b5cf6",
	practice: "#3b82f6",
	flashcards: "#fb923c",
};

const distributionColors = {
	excellent: "#4ade80",
	good: "#3b82f6",
	regular: "#fbbf24",
	low: "#fb7185",
};

const periodOptions = [7, 30, 90];

function clampPercentage(value) {
	const parsed = Number(value);
	if (!Number.isFinite(parsed)) return 0;
	return Math.max(0, Math.min(100, Math.round(parsed)));
}

function formatInteger(value) {
	return new Intl.NumberFormat("es-MX").format(Number(value) || 0);
}

function formatDelta(value, suffix = "") {
	const numeric = Number(value) || 0;
	return `${numeric > 0 ? "+" : ""}${numeric}${suffix}`;
}

function SectionCard({ children, sx }) {
	return (
		<Paper
			elevation={0}
			sx={{
				height: "100%",
				border: "1px solid #e2e8f0",
				borderRadius: "22px",
				p: { xs: 1.75, md: 2.15 },
				boxShadow: "0 18px 50px rgba(15, 23, 42, 0.06)",
				backgroundColor: "#ffffff",
				backgroundImage: "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.72) 100%)",
				...sx,
			}}
		>
			{children}
		</Paper>
	);
}

function SectionHeader({ title, actionLabel, onAction }) {
	return (
		<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
			<Typography sx={{ color: "#0f172a", fontSize: { xs: 16, md: 18 }, fontWeight: 600, letterSpacing: "-0.01em" }}>{title}</Typography>
			{actionLabel ? (
				<Button
					variant="text"
					onClick={onAction}
					sx={{ textTransform: "none", color: "#3b82f6", fontSize: 12, fontWeight: 600, minWidth: "auto", px: 0, "&:hover": { backgroundColor: "transparent", color: "#2563eb" } }}
				>
					{actionLabel}
				</Button>
			) : null}
		</Stack>
	);
}

function SummaryStatCard({ icon: Icon, iconColor, iconBg, label, value, delta }) {
	return (
		<SectionCard>
			<Stack spacing={1.65}>
				<Stack direction="row" alignItems="center" spacing={1.5}>
					<Box
						sx={{
							width: 50,
							height: 50,
							borderRadius: "14px",
							backgroundColor: iconBg,
							color: iconColor,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						<Icon size={22} strokeWidth={2.2} />
					</Box>
					<Box sx={{ display: "flex", flex: 1, flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
						<Typography sx={{ color: "#475569", fontSize: 14, fontWeight: 500, lineHeight: 1.2 }}>
							{label}
						</Typography>
						<Typography sx={{ color: "#0f172a", fontSize: { xs: 24, md: 28 }, fontWeight: 700, lineHeight: 1, letterSpacing: "-0.02em", mt: 0.65 }}>
							{value}
						</Typography>
					</Box>
				</Stack>

				<Stack direction="row" spacing={0.75} alignItems="center">
					<TrendingUp size={15} color="#22c55e" />
					<Typography sx={{ color: "#22c55e", fontSize: 12, fontWeight: 600 }}>{delta}</Typography>
					<Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 400 }}>vs. periodo anterior</Typography>
				</Stack>
			</Stack>
		</SectionCard>
	);
}

function EvolutionChart({ items }) {
	const rows = (items || []).map((item, index) => ({
		...item,
		index,
		accuracy: clampPercentage(item?.accuracy_percentage),
		answeredItems: Number(item?.answered_items ?? item?.total_items ?? item?.count ?? 0) || 0,
		label: item?.label || item?.date || `Día ${index + 1}`,
	})).filter((item) => item.accuracy > 0);
	const hasItems = rows.length > 0;
	const chartHeight = 230;

	return (
		<SectionCard sx={{ overflow: "hidden" }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
				<Typography sx={{ color: "#0f172a", fontSize: { xs: 16, md: 18 }, fontWeight: 600 }}>Evolución de precisión</Typography>
				<Box sx={{ border: "1px solid #e2e8f0", borderRadius: "12px", px: 1.4, py: 0.8, color: "#475569", display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 12, fontWeight: 600, backgroundColor: "#fff", boxShadow: "0 4px 12px rgba(15, 23, 42, 0.04)", minWidth: 88, justifyContent: "center" }}>
					Diaria
					<ChevronDown size={14} />
				</Box>
			</Stack>
			{!hasItems ? (
				<Box sx={{ minHeight: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
					<Typography sx={{ color: "#94a3b8", fontSize: 13, fontWeight: 500 }}>Sin datos para mostrar.</Typography>
				</Box>
			) : (
				<Box sx={{ display: "grid", gridTemplateColumns: "34px 1fr", gap: 1.5, alignItems: "stretch" }}>
					<Box sx={{ height: chartHeight, display: "flex", flexDirection: "column", justifyContent: "space-between", pt: 0.5, pb: 0.5 }}>
						{[100, 75, 50, 25, 0].map((tick) => (
							<Typography key={tick} sx={{ color: "#94a3b8", fontSize: 11, fontWeight: 600, lineHeight: 1, textAlign: "right" }}>
								{tick}%
							</Typography>
						))}
					</Box>

					<Box sx={{ overflowX: "auto", pb: 0.5 }}>
						<Box sx={{ minWidth: Math.max(520, rows.length * 58), position: "relative" }}>
							<Box
								sx={{
									height: chartHeight,
									display: "grid",
									gridTemplateColumns: `repeat(${rows.length}, minmax(42px, 1fr))`,
									gap: 1.25,
									alignItems: "end",
									px: 1,
									borderBottom: "1px solid #cbd5e1",
									backgroundImage: "linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)",
									backgroundSize: `100% ${chartHeight / 4}px`,
								}}
							>
								{rows.map((item) => (
									<Box key={item?.date || item?.label || item.index} sx={{ height: "100%", minWidth: 0, position: "relative" }}>
										<Typography sx={{ position: "absolute", left: 0, right: 0, bottom: `calc(${Math.max(6, item.accuracy)}% + 8px)`, color: "#0f172a", fontSize: 11, fontWeight: 700, lineHeight: 1, textAlign: "center" }}>{item.accuracy}%</Typography>
										<Box
											title={`${item.label}: ${item.accuracy}% (${formatInteger(item.answeredItems)} preguntas)`}
											sx={{
												position: "absolute",
												left: "50%",
												bottom: 0,
												transform: "translateX(-50%)",
												width: "70%",
												maxWidth: 36,
												height: `${Math.max(6, item.accuracy)}%`,
												borderRadius: "10px 10px 3px 3px",
												background: "linear-gradient(180deg, #8b5cf6 0%, #3b82f6 100%)",
												boxShadow: "0 10px 20px rgba(59, 130, 246, 0.18)",
												transition: "transform 160ms ease, filter 160ms ease",
												"&:hover": { transform: "translate(-50%, -3px)", filter: "brightness(1.04)" },
											}}
										/>
									</Box>
								))}
							</Box>

							<Box sx={{ display: "grid", gridTemplateColumns: `repeat(${rows.length}, minmax(42px, 1fr))`, gap: 1.25, px: 1, pt: 1 }}>
								{rows.map((item) => (
									<Box key={`${item?.date || item?.label || item.index}-label`} sx={{ minWidth: 0, textAlign: "center" }}>
										<Typography sx={{ color: "#475569", fontSize: 11, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</Typography>
										<Typography sx={{ color: "#94a3b8", fontSize: 10, fontWeight: 600, mt: 0.25 }}>{formatInteger(item.answeredItems)} preg.</Typography>
									</Box>
								))}
							</Box>
						</Box>
					</Box>
				</Box>
			)}
		</SectionCard>
	);
}

function SpecialtyPerformance({ items, onViewAll }) {
	const colors = ["#8b5cf6", "#3b82f6", "#4ade80", "#fb923c", "#fb7185"];

	return (
		<SectionCard sx={{ display: "flex", flexDirection: "column" }}>
			<SectionHeader title="Rendimiento por materia" actionLabel="Ver todas" onAction={onViewAll} />
			<Stack spacing={2.25} sx={{ flex: 1, justifyContent: "space-between" }}>
				{(items || []).slice(0, 5).map((item, index) => (
					<Stack key={item?.specialty_id || item?.specialty_name || index} direction="row" spacing={2} alignItems="center">
						<Typography sx={{ color: "#334155", fontSize: 14, fontWeight: 500, width: 142, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{item?.specialty_name}
						</Typography>
						<Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}>
							<Box sx={{ width: "100%", height: 8, borderRadius: 999, backgroundColor: "#edf2f7", overflow: "hidden" }}>
								<Box sx={{ width: `${clampPercentage(item?.accuracy_percentage)}%`, height: "100%", borderRadius: 999, backgroundColor: colors[index % colors.length] }} />
							</Box>
						</Box>
						<Typography sx={{ color: "#334155", fontSize: 13, fontWeight: 600, width: 44, textAlign: "right", flexShrink: 0 }}>
							{clampPercentage(item?.accuracy_percentage)}%
						</Typography>
						<Typography sx={{ color: "#94a3b8", fontSize: 11, fontWeight: 500, width: 96, textAlign: "right", flexShrink: 0 }}>
							{formatInteger(item?.answered_items)} preguntas
						</Typography>
					</Stack>
				))}
			</Stack>
		</SectionCard>
	);
}

function DonutChart({ segments, totalLabelTop, totalLabelBottom, colorMap }) {
	const gradient = (segments || []).reduce(
		(acc, item) => {
			const percentage = Number(item?.percentage) || 0;
			const start = acc.total;
			const end = start + percentage;
			const color = colorMap[item?.type || item?.key] || "#cbd5e1";
			acc.parts.push(`${color} ${start}% ${end}%`);
			acc.total = end;
			return acc;
		},
		{ total: 0, parts: [] }
	);

	if (gradient.total < 100) {
		gradient.parts.push(`#e5e7eb ${gradient.total}% 100%`);
	}

	return (
		<Box
			sx={{
				width: 146,
				height: 146,
				mx: { xs: "auto", md: 0 },
				alignSelf: { xs: "center", md: "auto" },
				borderRadius: "50%",
				background: `conic-gradient(${gradient.parts.join(", ")})`,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				flexShrink: 0,
			}}
		>
			<Box
				sx={{
					width: 112,
					height: 112,
					borderRadius: "50%",
					backgroundColor: "#ffffff",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				{totalLabelTop ? <Typography sx={{ color: "#64748b", fontSize: 11, fontWeight: 600 }}>{totalLabelTop}</Typography> : null}
				{totalLabelBottom ? <Typography sx={{ color: "#0f172a", fontSize: 26, fontWeight: 700, lineHeight: 1 }}>{totalLabelBottom}</Typography> : null}
			</Box>
		</Box>
	);
}

function ActivityByType({ items }) {
	const totalCount = (items || []).reduce((acc, item) => acc + (Number(item?.count) || 0), 0);

	return (
		<SectionCard>
			<SectionHeader title="Actividad por tipo" />
			<Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems="center">
				<DonutChart segments={items} totalLabelTop="Total" totalLabelBottom={String(totalCount)} colorMap={activityColors} />
				<Stack spacing={1.5} sx={{ width: "100%" }}>
					{(items || []).map((item) => (
						<Stack key={item?.type} direction="row" alignItems="center" spacing={1.25}>
							<Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: activityColors[item?.type] || "#cbd5e1" }} />
							<Typography sx={{ color: "#334155", fontSize: 13, fontWeight: 500, flex: 1 }}>{item?.label}</Typography>
							<Typography sx={{ color: "#64748b", fontSize: 10, fontWeight: 600 }}>{item?.percentage}% ({item?.count})</Typography>
						</Stack>
					))}
					<Typography sx={{ color: "#94a3b8", fontSize: 11, fontWeight: 500 }}>Preguntas</Typography>
				</Stack>
			</Stack>
		</SectionCard>
	);
}

function StudyStreak({ streak }) {
	const days = streak?.week_activity || [];

	return (
		<SectionCard sx={{ overflow: "hidden" }}>
			<SectionHeader title="Racha de estudio" />
			<Stack spacing={1.5}>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<Box sx={{ color: "#fb923c", display: "inline-flex", flexShrink: 0 }}>
						<Flame size={28} />
					</Box>
					<Box sx={{ minWidth: 0 }}>
						<Typography sx={{ color: "#0f172a", fontSize: 16, fontWeight: 700 }}>{formatInteger(streak?.current_streak)} días</Typography>
						<Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 500 }}>Mejor racha: {formatInteger(streak?.best_streak)} días</Typography>
					</Box>
				</Stack>

				<Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", justifyItems: "center", gap: { xs: 0.5, md: 0.75 }, width: "100%", maxWidth: { xs: 260, md: "none" }, mx: { xs: "auto", md: 0 } }}>
					{days.map((day, index) => (
						<Stack key={day?.date || index} spacing={0.5} alignItems="center" sx={{ minWidth: 0, width: { xs: 22, md: 20, lg: 24 } }}>
							<Box
								sx={{
									width: { xs: 22, md: 20, lg: 24 },
									height: { xs: 22, md: 20, lg: 24 },
									borderRadius: "50%",
									border: day?.has_activity ? "none" : "1px solid #cbd5e1",
									backgroundColor: day?.has_activity ? "#4ade80" : "#ffffff",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									color: day?.has_activity ? "#ffffff" : "#cbd5e1",
								}}
							>
								{day?.has_activity ? <CheckCircle2 size={13} /> : null}
							</Box>
							<Typography sx={{ color: "#94a3b8", fontSize: 10, fontWeight: 600, overflow: "hidden", textAlign: "center", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}>{day?.label}</Typography>
						</Stack>
					))}
				</Box>

				<Typography sx={{ color: "#7c3aed", fontSize: 13, fontWeight: 600 }}>¡Sigue así, vas por buen camino!</Typography>
			</Stack>
		</SectionCard>
	);
}

function AccuracyDistribution({ distribution, averageAccuracy }) {
	const entries = Object.entries(distribution || {}).map(([key, value]) => ({ key, ...value }));
	const average = clampPercentage(averageAccuracy);

	return (
		<SectionCard>
			<SectionHeader title="Distribución de precisión" />
			<Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems="center">
				<DonutChart segments={entries} totalLabelTop="Promedio" totalLabelBottom={`${average}%`} colorMap={distributionColors} />
				<Stack spacing={1.4} sx={{ width: "100%" }}>
					{entries.map((item) => (
						<Stack key={item?.key} direction="row" spacing={1.25} alignItems="center">
							<Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: distributionColors[item?.key] || "#cbd5e1" }} />
							<Typography sx={{ color: "#334155", fontSize: 13, fontWeight: 500, flex: 1 }}>{item?.label}</Typography>
							<Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 600 }}>{item?.percentage}% ({item?.count})</Typography>
						</Stack>
					))}
				</Stack>
			</Stack>
		</SectionCard>
	);
}

function GeneralSummary({ summary }) {
	const items = [
		{ label: "Preguntas respondidas", value: formatInteger(summary?.answered_items), color: "#8b5cf6", bg: "#f3e8ff", icon: Target },
		{ label: "Correctas", value: formatInteger(summary?.correct_items), color: "#22c55e", bg: "#dcfce7", icon: CheckCircle2 },
		{ label: "Incorrectas", value: formatInteger(summary?.incorrect_items), color: "#fb7185", bg: "#ffe4e6", icon: HelpCircle },
	];

	return (
		<SectionCard>
			<SectionHeader title="Resumen general" />
			<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 2 }}>
				{items.map((item) => {
					const Icon = item.icon;
					return (
						<Stack key={item.label} direction="row" spacing={1.25} alignItems="center" sx={{ p: 0.5 }}>
							<Box sx={{ width: 34, height: 34, borderRadius: "50%", backgroundColor: item.bg, color: item.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
								<Icon size={16} />
							</Box>
							<Box>
								<Typography sx={{ color: "#0f172a", fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{item.value}</Typography>
								<Typography sx={{ color: "#64748b", fontSize: 11, fontWeight: 400 }}>{item.label}</Typography>
							</Box>
						</Stack>
					);
				})}
			</Box>
		</SectionCard>
	);
}

function ComparisonCard({ comparison }) {
	const items = [
		{ label: "Precisión", value: formatDelta(comparison?.accuracy_delta, "%") },
		{ label: "Preguntas", value: formatDelta(comparison?.answered_items_delta) },
		{ label: "Correctas", value: formatDelta(comparison?.correct_items_delta) },
	];

	return (
		<SectionCard>
			<SectionHeader title="Comparación con período anterior" />
			<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 2 }}>
				{items.map((item) => (
					<Box key={item.label} sx={{ p: 0.5 }}>
						<Typography sx={{ color: "#22c55e", fontSize: 24, fontWeight: 700, lineHeight: 1 }}>{item.value}</Typography>
						<Typography sx={{ color: "#64748b", fontSize: 11, fontWeight: 500, mt: 0.6 }}>{item.label}</Typography>
					</Box>
				))}
			</Box>
		</SectionCard>
	);
}

export default function Estadisticas() {
	const history = useHistory();
	const [days, setDays] = useState(30);
	const [payload, setPayload] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState("");
	const [showSplash, setShowSplash] = useState(true);

	useEffect(() => {
		let mounted = true;

		async function fetchStatistics() {
			setIsLoading(true);
			setErrorMessage("");

			try {
				const { data, error } = await getStatisticsDashboard(days);
				if (!mounted) return;

				if (error) {
					console.error("Error fetching statistics dashboard:", error);
					setErrorMessage("No se pudieron cargar las estadísticas.");
					setPayload(null);
					return;
				}

				setPayload(data || {});
			} catch (error) {
				if (!mounted) return;
				console.error("Unexpected error fetching statistics dashboard:", error);
				setErrorMessage("Ocurrió un error al cargar las estadísticas.");
				setPayload(null);
			} finally {
				if (mounted) setIsLoading(false);
			}
		}

		fetchStatistics();

		return () => {
			mounted = false;
		};
	}, [days]);

	useEffect(() => {
		if (isLoading) return undefined;

		// let the logo mouth-open/close animation finish before revealing the app
		const timeoutId = setTimeout(() => setShowSplash(false), 950);
		return () => clearTimeout(timeoutId);
	}, [isLoading]);

	const summary = payload?.summary || {};
	const comparison = payload?.comparison || {};
	const evolution = payload?.precision_evolution || [];
	const specialtyItems = payload?.performance_by_specialty || [];
	const activityItems = payload?.activity_by_type || [];
	const distribution = payload?.accuracy_distribution || {};
	const studyStreak = payload?.study_streak || {};
	const generalSummary = payload?.general_summary || {};
	const handleViewSpecialties = () => {
		history.push("/admin/especialidades");
	};

	return (
		<Box sx={{ mb: 5 }}>
			<Stack
				direction={{ xs: "column", lg: "row" }}
				alignItems={{ xs: "flex-start", lg: "center" }}
				justifyContent="space-between"
				spacing={2}
				sx={{ mb: 3 }}
			>
				<Box>
					<Typography sx={{ color: "#0f172a", fontSize: { xs: 26, md: 31 }, fontWeight: 700, lineHeight: 1.05 }}>Estadísticas</Typography>
					<Typography sx={{ color: "#64748b", fontSize: 14, fontWeight: 400, mt: 0.8 }}>
						Tu progreso en ENARM Apex
					</Typography>
				</Box>

				<Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ width: { xs: "100%", lg: "auto" } }}>
					<Box sx={{ minWidth: 210 }}>
						<Select
							value={days}
							onChange={(event) => setDays(Number(event.target.value))}
							IconComponent={ChevronDown}
							renderValue={(selected) => (
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#334155" }}>
									<Box sx={{ display: "inline-flex", alignItems: "center", color: "#64748b", lineHeight: 0 }}>
										<CalendarDays size={16} />
									</Box>
									<Box component="span">Últimos {selected} días</Box>
								</Box>
							)}
							size="small"
							fullWidth
							sx={{
								borderRadius: "14px",
								backgroundColor: "#ffffff",
								boxShadow: "0 6px 20px rgba(15, 23, 42, 0.04)",
								".MuiSelect-select": {
									display: "flex",
									alignItems: "center",
									py: 1.15,
									pl: 1.6,
									pr: 4.5,
									fontSize: 13,
									fontWeight: 600,
									color: "#334155",
								},
								".MuiSelect-icon": {
									color: "#64748b",
									right: 12,
									top: "calc(50% - 0.5em)",
								},
								".MuiOutlinedInput-notchedOutline": {
									borderColor: "#e2e8f0",
								},
								"&:hover .MuiOutlinedInput-notchedOutline": {
									borderColor: "#cbd5e1",
								},
								"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
									borderColor: "#8b5cf6",
									borderWidth: 1,
								},
								"& .MuiSvgIcon-root": {
									fontSize: 18,
								},
							}}
						>
							{periodOptions.map((option) => (
								<MenuItem key={option} value={option}>
									Últimos {option} días
								</MenuItem>
							))}
						</Select>
					</Box>

				</Stack>
			</Stack>

			{errorMessage ? (
				<Alert severity="error" sx={{ mb: 3, borderRadius: "14px" }}>
					{errorMessage}
				</Alert>
			) : null}

			{showSplash ? (
				<SectionCard sx={{ minHeight: 360, p: 4, width: "100%" }}>
					<div style={{ alignItems: "center", display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 280, width: "100%" }}>
						<LogoLoader size={110} label="Cargando estadísticas..." fullScreen={false} done={!isLoading} />
					</div>
				</SectionCard>
			) : (
				<Stack spacing={2.5}>
					<Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" } }}>
						<SummaryStatCard icon={Target} iconColor="#8b5cf6" iconBg="#f3e8ff" label="Precisión promedio" value={`${clampPercentage(summary?.accuracy_percentage)}%`} delta={formatDelta(comparison?.accuracy_delta, "%")} />
						<SummaryStatCard icon={CalendarDays} iconColor="#3b82f6" iconBg="#dbeafe" label="Preguntas respondidas" value={formatInteger(summary?.answered_items)} delta={formatDelta(comparison?.answered_items_delta)} />
						<SummaryStatCard icon={CheckCircle2} iconColor="#4ade80" iconBg="#dcfce7" label="Respuestas correctas" value={formatInteger(summary?.correct_items)} delta={formatDelta(comparison?.correct_items_delta)} />
					</Box>

					<Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "1.35fr 1fr" } }}>
						<EvolutionChart items={evolution} />
						<SpecialtyPerformance items={specialtyItems} onViewAll={handleViewSpecialties} />
					</Box>

					<Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" } }}>
						<ActivityByType items={activityItems} />
						<StudyStreak streak={studyStreak} />
						<AccuracyDistribution distribution={distribution} averageAccuracy={summary?.accuracy_percentage} />
					</Box>

					<Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "1.2fr 1fr" } }}>
						<GeneralSummary summary={generalSummary} />
						<ComparisonCard comparison={comparison} />
					</Box>
				</Stack>
			)}
		</Box>
	);
}
